import React, { PureComponent } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import Fade from '@mui/material/Fade';
import Table from '@mui/material/Table';
import Zoom from '@mui/material/Zoom';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Pagination from '@mui/lab/Pagination/Pagination';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import { NavLink, Navigate } from 'react-router-dom';
import { faCut } from '@fortawesome/free-solid-svg-icons';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  Container,
  InfoContainer,
  NoItemsText,
  PageStatusContainer,
  PaginationContainer,
  PaginationInfo,
  PageSizeSelect,
  StatusIcon,
  TextContainer,
  NavLinkContainer,
  StyledClipperButton,
  FilterRow,
  ColumnFilterTextField,
  ColumnFilterDatePicker,
  ColumnFilterSelect,
  ColumnFilterAutoComplete,
  ClearFiltersButton,
  TableScrollContainer,
} from './styles';
import { StatusContainer } from '../../Shared/JobList/styles';
import {
  KeyRoleUserAccessPermission,
  selectUserOrgsSecurityProfilesIds, selectUserRole, selectIsUserVaultSuperAdmin, updateUseAs,
} from '../../../state/user/userOrgRole';

import { IAppState } from '../../../state/reducers';
import {
  IDispatchProps, IProps, IState, IStateProps,
} from './interfaces';
import { FaIcon } from '../../Shared/Icons';
import LiveEventMenu from '../LiveEventMenu/component';
import Header from './Header';
import { AssetHit } from '../../../api/vault/searchEngine';
import { updateSearchQueryParams } from '../../../state/search';

import {
  openAssetModal,
  closeNewAssetModal,
  updateLiveAssetModalProperties,
} from '../../../state/liveEvents/liveAssetModal';
import { searchLiveAssets, startBackgroundSearchInterval, clearBackgroundSearchInterval } from '../../../state/liveEvents/liveEventsList';
import { deleteSelectedAssets as deleteAssets, terminateLiveStream } from '../../../state/deleteAssets';
import { updateLiveFilterValues, clearAllFilters } from '../../../state/filterAssets';
import { Routes } from '../../../constants';
import { ISecurityProfile } from '../../../api/vault/securityProfiles';
import { StreamStatus } from '../../../enums';
import LiveEventModals from '../LiveEventsModals/component';
import { parseDatefromString } from '../../../utils/time';
import { labelForStreamStatus, tooltipForStreamStatus } from '../../Shared/LiveEvents/statusToLabelMap';

const streamStatusOptions = Object.values(StreamStatus).map((status) => ({
  label: labelForStreamStatus(status),
  value: status.toString(),
}));
streamStatusOptions.unshift({ label: 'All', value: '' });

export const getStreamTitle = (asset: AssetHit) => {
  const bitrate = `- (Bitrate: ${(asset._source.streamBitrate ? `${asset._source.streamBitrate}kb/s` : 'Auto')})`;
  if (asset._source.streamChannel) {
    return (
      <Tooltip placement="top" TransitionComponent={Zoom} arrow title={asset._source.streamUrl}>
        <TextContainer>{`${asset._source.streamChannel} ${bitrate}`}</TextContainer>
      </Tooltip>
    );
  }
  return `${asset._source.streamUrl} ${bitrate}`;
};

export const getStatusOfLiveEvent = (item: AssetHit) => StreamStatus[item._source.streamStatus as keyof typeof StreamStatus];

export const getSecurityProfileName = (asset: AssetHit, securityProfilesMapping: ISecurityProfile[]) => {
  const securityProfile = securityProfilesMapping.find((sp: ISecurityProfile) => sp._id === asset._source.securityProfiles[0]);
  return securityProfile
    ? securityProfile.name
    : 'No Security Profile';
};

export const handleClipper = (asset: AssetHit, target: EventTarget & HTMLAnchorElement) => {
  // eslint-disable-next-line no-param-reassign
  target.href = `${Routes.VIRTUAL_CLIPPER}?assetId=${asset._id}&timeStamp=${0}`;
};

class LiveEventsList extends PureComponent<IProps, IState> {
  private was_filtered = false;

  private static readonly DESC = 'desc';
  private static readonly ASC = 'asc';

  private readonly DEFAULT_RESULTS_LIMIT_SIZE = 25;
  private readonly DEFAULT_PAGE = 1;

  async componentDidMount() {
    const {
      updateSearchQueryParams,
      searchLiveAssets,
      startBackgroundSearchInterval,
      updateLiveFilterValues,
      filters,
    } = this.props;

    document.title = 'Live Events';

    const todayStart = moment().startOf('day').utc().toISOString();
    const todayEnd = moment().endOf('day').utc().toISOString();

    updateLiveFilterValues({
      ...filters,
      start: [todayStart, todayEnd],
    });

    updateSearchQueryParams({
      size: this.DEFAULT_RESULTS_LIMIT_SIZE,
      page: this.DEFAULT_PAGE,
      sortField: 'start',
      sortOrder: 'asc',
    });

    searchLiveAssets();
    startBackgroundSearchInterval();
  }

  componentWillUnmount() {
    document.title = 'Vault';
    const { clearBackgroundSearchInterval } = this.props;
    clearBackgroundSearchInterval();
    this.debouncedSearch.cancel();
  }

  componentDidUpdate(prevProps: IProps) {
    const {
      selectedUseAs,
      allUsers,
      updateUseAsUser,
      isLiveVideo,
      searchLiveAssets,
    } = this.props;

    const isUseAsAdded = isEmpty(prevProps.selectedUseAs) && !isEmpty(selectedUseAs);
    const isUseAsChanged = !isEmpty(prevProps.selectedUseAs) && !isEqual(selectedUseAs, prevProps.selectedUseAs);
    const isLiveVideoChanged = isLiveVideo !== prevProps.isLiveVideo;

    if (isUseAsAdded || isUseAsChanged || isLiveVideoChanged) {
      updateUseAsUser(selectedUseAs, allUsers);
      searchLiveAssets();
    }
  }

  private handleRequestSort = async (column: string) => {
    const {
      queryParams: {
        sortField,
        sortOrder,
        size,
        page,
      },
      updateSearchQueryParams,
      searchLiveAssets,
    } = this.props;
    const { ASC, DESC } = LiveEventsList;
    const isDesc = sortField === column && sortOrder === DESC;

    updateSearchQueryParams({
      sortField: column,
      sortOrder: isDesc ? ASC : DESC,
      size,
      page,
    });

    searchLiveAssets();
  };

  private debouncedSearch = _.debounce(() => {
    const { searchLiveAssets: refreshAssets } = this.props;
    refreshAssets();
  }, 400);

  private handleFilterChange = (value: string | Date | Array<string> | boolean | null, columnName: string) => {
    const {
      filters,
      updateLiveFilterValues,
      updateSearchQueryParams,
      searchLiveAssets: refreshAssets,
    } = this.props;

    updateSearchQueryParams({ page: 1 });

    updateLiveFilterValues({
      ...filters,
      [columnName]: value,
    });

    if (columnName === 'textSearch') {
      this.debouncedSearch();
    } else {
      setTimeout(() => { refreshAssets(); }, 100);
    }
  };

  private generateFilterRow = () => {
    const {
      filters,
      securityProfileOptions,
      isSuperAdmin,
      clearAllFilters: clearAllFiltersConnect,
    } = this.props;

    const selectedSecurityProfiles = filters.securityProfiles ?? [];

    return (
      <FilterRow>
        <TableCell className="title">
          <ColumnFilterTextField
            placeholder="Search title..."
            variant="outlined"
            size="small"
            value={filters.textSearch ?? ''}
            onChange={(e) => this.handleFilterChange(e.target.value, 'textSearch')}
          />
        </TableCell>
        <TableCell className="stream" />
        <TableCell className="security-profile">
          {isSuperAdmin && (
            <ColumnFilterAutoComplete
              options={securityProfileOptions}
              value={selectedSecurityProfiles}
              onChange={(e: any) => this.handleFilterChange(e ?? [], 'securityProfiles')}
              className={selectedSecurityProfiles.length ? '' : 'placeholder'}
              isClearable
              menuPortalTarget={document.body}
              menuPlacement="bottom"
              placeholder="All"
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
          )}
        </TableCell>
        <TableCell className="start-time">
          <ColumnFilterDatePicker
            format="MM-dd-yyyy"
            value={filters.start ? parseDatefromString(filters.start[0]) : null}
            onChange={(e) => {
              if (!e) {
                this.handleFilterChange(null, 'start');
                return;
              }
              const dateValid = new Date(e as string | number);
              const start = moment(dateValid).startOf('day').utc();
              const end = moment(dateValid).endOf('day').utc();
              this.handleFilterChange([start.toISOString(), end.toISOString()], 'start');
            }}
            data-auto="FilterDateInput"
            slotProps={{
              actionBar: { actions: ['clear', 'today'] },
              textField: { size: 'small', placeholder: 'Filter date...' },
            }}
          />
        </TableCell>
        <TableCell className="duration" />
        <TableCell className="status">
          <ColumnFilterSelect
            value={filters.streamStatus ?? ''}
            onChange={(e: any) => this.handleFilterChange(e.target.value, 'streamStatus')}
            displayEmpty
            size="small"
            variant="outlined"
            renderValue={(selectedStreamStatus) => {
              if (!selectedStreamStatus || selectedStreamStatus === '') {
                return <span style={{ color: '#999' }}>All</span>;
              }
              return streamStatusOptions.find((x) => x.value === selectedStreamStatus.toString())?.label || 'All';
            }}
          >
            {streamStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </ColumnFilterSelect>
        </TableCell>
        <TableCell className="clip" />
        <TableCell className="options">
          <ClearFiltersButton
            variant="plainOnWhite"
            onClick={() => {
              clearAllFiltersConnect();
              const { searchLiveAssets: refreshAssets } = this.props;
              setTimeout(() => { refreshAssets(); }, 100);
            }}
            data-auto="FilterClearBtn"
          >
            Clear Filters
          </ClearFiltersButton>
        </TableCell>
      </FilterRow>
    );
  };

  private generateTableHeader = () => {
    const {
      queryParams: {
        sortOrder,
        sortField,
      },
    } = this.props;

    return (
      <TableHead>
        <TableRow>
          <TableCell className="title">
            <TableSortLabel
              id="liveEvents.Title.Sort"
              active={sortField === 'title'}
              direction={sortOrder}
              onClick={(e) => this.handleRequestSort('title')}
            >
              Event Title
            </TableSortLabel>
          </TableCell>
          <TableCell className="stream">
            <TableSortLabel
              id="liveEvents.URL.Sort"
              active={sortField === 'streamUrl'}
              direction={sortOrder}
              onClick={(e) => this.handleRequestSort('streamUrl')}
            >
              Stream
            </TableSortLabel>
          </TableCell>
          <TableCell className="security-profile">
            Security Profile
          </TableCell>
          <TableCell className="start-time">
            <TableSortLabel
              id="liveEvents.StartTime.Sort"
              active={sortField === 'start'}
              direction={sortOrder}
              onClick={(e) => this.handleRequestSort('start')}
            >
              Start ({moment().tz(moment.tz.guess()).format('zz')})
            </TableSortLabel>
          </TableCell>
          <TableCell className="duration">Duration (mins)</TableCell>
          <TableCell className="status">
            <TableSortLabel
              id="liveEvents.Status.Sort"
              active={sortField === 'streamStatus'}
              direction={sortOrder}
              onClick={(e) => this.handleRequestSort('streamStatus')}
            >
              Status
            </TableSortLabel>
          </TableCell>
          <TableCell className="clip">Clip</TableCell>
          <TableCell className="options" />
        </TableRow>
        {this.generateFilterRow()}
      </TableHead>
    );
  };

  private generateBody() {
    if (this.props.searchResults) {
      const { searchResults: { hits: { hits: liveEvents } } } = this.props;
      const { securityProfilesMapping } = this.props;

      if (liveEvents.length > 0) {
        return liveEvents.map((item) => (
          <TableRow key={item._id}>
            <TableCell>
              {item._source.title}
            </TableCell>
            <TableCell className="stream">
              {getStreamTitle(item)}
            </TableCell>
            <TableCell>
              {getSecurityProfileName(item, securityProfilesMapping)}
            </TableCell>
            <TableCell>
              <div>{moment(item._source.start).format('ll')}</div>
              <div>{moment(item._source.start).format('LT')}</div>
            </TableCell>
            <TableCell>
              {this.calculateDuration(item)}
            </TableCell>
            <TableCell>
              <StatusContainer>
                <Tooltip
                  title={tooltipForStreamStatus(item._source.streamStatus, item._source.streamErrorReason)}
                  aria-label="Stream status"
                >
                  <StatusIcon status={getStatusOfLiveEvent(item)} />
                </Tooltip>
              </StatusContainer>
            </TableCell>
            <TableCell>
              <NavLinkContainer>
                <NavLink
                  target="_blank"
                  to={Routes.VIRTUAL_CLIPPER}
                  onClick={(e) => handleClipper(item, e.currentTarget)}
                >
                  <StyledClipperButton
                    id="AssetDetails.OperationArea.NewClip"
                    variant="transparentOnWhite"
                    data-tip="Clip Editor"
                    disabled={(item._source.streamStatus !== StreamStatus.Complete && item._source.streamStatus !== StreamStatus.Live)}
                    data-for="operations-tooltip"
                  >
                    <FaIcon icon={faCut} />
                  </StyledClipperButton>
                </NavLink>
              </NavLinkContainer>
            </TableCell>
            <TableCell>
              <LiveEventMenu asset={item} />
            </TableCell>
          </TableRow>
        ));
      } return this.renderNoItemsInfo();
    }

    return this.renderNoItemsInfo();
  }

  calculateDuration = (item: AssetHit) => {
    const duration = moment.duration(moment(item._source.stop).diff(moment(item._source.start)));
    return Math.floor(duration.asMinutes());
  };

  closeEditDialog = () => {
    const { closeAssetModal } = this.props;
    closeAssetModal();
  };

  renderNoItemsInfo = () => (
    <Fade in>
      <InfoContainer>
        <NoItemsText data-auto="NoEventsAvailableMsg">No Live Events Available</NoItemsText>
      </InfoContainer>
    </Fade>
  );

  renderPagination() {
    const { searchResults, queryParams: { size, page } } = this.props;

    const total = searchResults?.hits?.total?.value ?? 0;
    const hitCount = searchResults?.hits?.hits?.length ?? 0;

    const startRecord = hitCount > 0 ? ((page - 1) * size) + 1 : 0;
    const endRecord = hitCount > 0 ? startRecord + hitCount - 1 : 0;

    const maxResult = 10000;
    const assetsCount = total > maxResult ? maxResult : total;
    const pagesCount = total > maxResult ? Math.max(Math.floor(assetsCount / size), 1) : Math.ceil(assetsCount / size);

    return (
      <PageStatusContainer>
        <PaginationInfo>
          <span>{total > 0 ? `${startRecord}–${endRecord} of ${total.toLocaleString()}` : '0 results'}</span>
          <span>Rows per page:</span>
          <PageSizeSelect
            value={size}
            onChange={(e: any) => {
              const { updateSearchQueryParams, searchLiveAssets: refreshAssets } = this.props;
              updateSearchQueryParams({ size: e.target.value, page: 1 });
              setTimeout(() => { refreshAssets(); }, 100);
            }}
            size="small"
            variant="outlined"
          >
            {[10, 25, 50, 100].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </PageSizeSelect>
        </PaginationInfo>
        <PaginationContainer>
          <Pagination
            count={pagesCount}
            page={page}
            onChange={this.handleChange}
            siblingCount={4}
            color="primary"
          />
        </PaginationContainer>
      </PageStatusContainer>
    );
  }

  handleChange = async (e: any, value: number) => {
    const {
      queryParams: {
        sortField,
        sortOrder,
        size,
      },
      updateSearchQueryParams,
      searchLiveAssets: refreshAssets,
    } = this.props;

    await updateSearchQueryParams({
      sortField,
      sortOrder,
      size,
      page: value,
    });

    refreshAssets();
  };

  render() {
    const {
      userRole,
    } = this.props;

    const hasManageLiveVideoPermission = userRole?.userAccessPermission?.addLiveVideo ?? false;

    if (hasManageLiveVideoPermission === false) {
      return (
        <Navigate to={Routes.HOME} replace />
      );
    }

    return (
      <Fade in>
        <Container>
          <Header />

          <TableScrollContainer>
            <Table>
              {this.generateTableHeader()}
              {this.generateBody()}
            </Table>
          </TableScrollContainer>
          {this.renderPagination()}
          <LiveEventModals />
        </Container>
      </Fade>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  queryParams: state.search.queryParams,
  isOpened: state.liveAssetModal?.isOpened ?? false,
  userOrgs: selectUserOrgsSecurityProfilesIds(state),
  userRole: selectUserRole(state),
  securityProfilesMapping: state.securityProfiles?.idValueMapping ?? [],
  selectedUseAs: state.user?.impersonation?.user,
  allUsers: state.user?.userOrgRole?.users ?? [],
  filterAssets: state.filterAssets,
  isLiveVideo: state.liveEvents?.isLiveVideo ?? false,
  searchResults: state.liveEventsList?.searchResults,
  filters: state.filterAssets?.liveAssetFilters,
  securityProfileOptions: state.securityProfiles?.options ?? [],
  isSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  openAssetModal,
  deleteAsset: deleteAssets,
  terminateLiveStream,
  updateSearchQueryParams,
  updateLiveAssetModalProperties,
  closeAssetModal: closeNewAssetModal,
  updateUseAsUser: updateUseAs,
  searchLiveAssets,
  startBackgroundSearchInterval,
  clearBackgroundSearchInterval,
  updateLiveFilterValues,
  clearAllFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(LiveEventsList);
